import { useLayoutEffect, useRef, useState } from "react";

interface RowFitInput {
  /** Available width of the row (container `clientWidth`). */
  available: number;
  /** Gap between adjacent items, matching the CSS `gap`. */
  gap: number;
  /** Width of the leading element (e.g. a label) rendered before the items. */
  leadingWidth: number;
  /** Intrinsic width of each item, in display order. */
  itemWidths: number[];
  /** Width of the "+N" overflow pill excluding its digits. */
  pillBaseWidth: number;
  /** Width added per digit of the "+N" overflow pill. */
  pillDigitWidth: number;
}

/**
 * Pure single-row fit calculation.
 *
 * Greedily fits as many items as possible on one row after the leading element.
 * When items overflow, reserves room for a numeric "+N" overflow pill — sized to
 * the *actual* hidden count — dropping trailing items until both the remaining
 * items and the pill fit.
 *
 * Kept free of the DOM so it can be unit tested; `useRowFit` supplies the
 * measured widths.
 */
export function computeVisibleCount(input: RowFitInput): number {
  const {
    available,
    gap,
    leadingWidth,
    itemWidths,
    pillBaseWidth,
    pillDigitWidth,
  } = input;
  const total = itemWidths.length;

  let used = leadingWidth;
  let count = 0;
  for (const width of itemWidths) {
    if (used + gap + width > available) break;
    used += gap + width;
    count++;
  }

  if (count < total) {
    const pillWidth = (hidden: number) =>
      pillBaseWidth + String(hidden).length * pillDigitWidth;
    let needed = gap + pillWidth(total - count);
    while (count > 0 && used + needed > available) {
      used -= gap + (itemWidths[count - 1] ?? 0);
      count--;
      needed = gap + pillWidth(total - count);
    }
  }

  return count;
}

/**
 * Measures how many items fit on a single row of a container and collapses the
 * rest behind a numeric "+N" overflow pill. Recomputes on container resize and
 * whenever `signature` changes (anything that alters a rendered width — the item
 * set, order, or contents).
 *
 * The consumer renders two rows sharing the same styles: the visible row on
 * `containerRef`, and an off-screen measurement row on `measureRef` laid out at
 * intrinsic width with this exact child order:
 *
 *   [leading, item0, …, itemN-1, pill "+8", pill "+88"]
 *
 * The two sample pills let us derive the pill's base and per-digit width so the
 * reserved overflow space matches the real "+N" label.
 */
// Intrinsic widths captured from the measurement row. These depend only on the
// content (`signature`), not the container width, so they are read once per
// content change and reused for every resize.
type RowMetrics = Pick<
  RowFitInput,
  "leadingWidth" | "itemWidths" | "pillBaseWidth" | "pillDigitWidth"
>;

export function useRowFit(itemCount: number, gap: number, signature: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<RowMetrics | null>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    // Read the intrinsic widths once for this content. Resizes reuse the cached
    // metrics, so the ResizeObserver path never touches the DOM (no reflow).
    // Use fractional widths (getBoundingClientRect), not the integer offsetWidth,
    // so summing many item widths doesn't accumulate rounding error and over-count
    // what fits (which would compress/wrap the last items).
    const widthOf = (el: HTMLElement | undefined) =>
      el?.getBoundingClientRect().width ?? 0;

    const measureMetrics = () => {
      const children = Array.from(measure.children) as HTMLElement[];
      // leading + items + two sample pills
      if (children.length !== itemCount + 3) return;
      const oneDigitPill = widthOf(children[children.length - 2]);
      const twoDigitPill = widthOf(children[children.length - 1]);
      const pillDigitWidth = Math.max(0, twoDigitPill - oneDigitPill);
      metricsRef.current = {
        leadingWidth: widthOf(children[0]),
        itemWidths: children.slice(1, itemCount + 1).map(widthOf),
        pillBaseWidth: oneDigitPill - pillDigitWidth,
        pillDigitWidth,
      };
    };

    const recompute = () => {
      const metrics = metricsRef.current;
      if (!metrics) return;
      setVisibleCount(
        computeVisibleCount({
          available: container.getBoundingClientRect().width,
          gap,
          ...metrics,
        })
      );
    };

    measureMetrics();
    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    // Widths measured before the web font finishes loading are too narrow, so
    // the row over-counts and clips; measure again once fonts have settled.
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (cancelled) return;
      measureMetrics();
      recompute();
    });
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [itemCount, gap, signature]);

  return { containerRef, measureRef, visibleCount };
}
